import { Dayjs } from 'dayjs';
import React, { useMemo, useCallback } from 'react';

import { Box } from '@mui/system';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';

import ElementLabel from './ElementLabel';
import { ElementProps } from '../../../@types/forms/types';

//-------------------------------------------------------------

type DateRangeElementProps = {
  el: ElementProps;
  pageNumberScale: number;
  isFormFillerPage: boolean;
  elementContentValue?: [Dayjs | null, Dayjs | null];
  handleDateRangeChange?: (el: ElementProps, newValue: [Dayjs | null, Dayjs | null]) => void;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const DateRangeElement: React.FC<DateRangeElementProps> = ({
  el,
  pageNumberScale,
  isFormFillerPage,
  elementContentValue,
  handleDateRangeChange,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  // Convert to stable string representation for memoization
  const dateKey = useMemo(() => {
    if (!elementContentValue) return 'null-null';
    const start = elementContentValue[0]?.format('YYYY-MM-DD') || 'null';
    const end = elementContentValue[1]?.format('YYYY-MM-DD') || 'null';
    return `${start}-${end}`;
  }, [elementContentValue]);

  // Only recalculate value when the actual dates change (not when object reference changes)
  const value = useMemo<[Dayjs | null, Dayjs | null]>(
    () => elementContentValue || [null, null],
    [dateKey]
  );

  const placeholder = useMemo(() => {
    if (!isFormFillerPage && el.dateRangeTemplate) {
      let result = el.dateRangeTemplate;
      result = result.replaceAll('[s]', 'YYYY-MM-DD');
      result = result.replaceAll('[e]', 'YYYY-MM-DD');
      result = result.replaceAll('[w]', '0');
      result = result.replaceAll('[c]', '0');
      return result;
    }

    if (!isFormFillerPage && !el.dateRangeTemplate) {
      return 'YYYY-MM-DD - YYYY-MM-DD';
    }

    return '';
  }, [isFormFillerPage, el.dateRangeTemplate]);

  const handleChange = useCallback(
    (newValue: [Dayjs | null, Dayjs | null]) => {
      if (handleDateRangeChange) {
        handleDateRangeChange(el, newValue);
      }
    },
    [handleDateRangeChange, el]
  );

  return (
    <>
      {isFormFillerPage && (
        <Box
          sx={{
            fontSize: `${el.fontSize * pageNumberScale}px`,
            width: el.width * pageNumberScale,
            backgroundColor: collaboratorColor,
            ...(!collaboratorColor && {
              borderRadius: '8px',
              padding: '8px 8px 0',
              width: '100%',
            }),
          }}
          className={collaboratorColor ? '' : 'date-range-bg'}
          id={el.id}
        >
          <DateRangePicker
            disabled={el.isChild || disabled}
            key={el.id}
            slots={{ field: SingleInputDateRangeField }}
            disablePast={el.disablePast}
            slotProps={{
              textField: {
                variant: 'standard',
                size: 'small',
                fullWidth: true,
                error: !!errorMessage,
                helperText: errorMessage || null,
                sx: {
                  '& .MuiPickersInputBase-sectionsContainer': {
                    fontFamily: el.font,
                    fontWeight: el.textStyle.includes('Bold') ? 'bold' : 'normal',
                    fontStyle: el.textStyle.includes('Italic') ? 'italic' : 'normal',
                    textDecoration: el.textDecoration === 1 ? 'underline' : 'none',
                    color: el.color,
                    fontSize: `${el.fontSize * pageNumberScale}px`,
                    lineHeight: `${el.fontSize * pageNumberScale * 1.2}px`,
                    justifyContent:
                      el.textAlignment === 1
                        ? 'center'
                        : el.textAlignment === 2
                          ? 'flex-end'
                          : 'flex-start',
                  },
                },
              },
            }}
            value={value}
            onChange={handleChange}
          />
        </Box>
      )}

      {!isFormFillerPage && (
        <ElementLabel
          el={el}
          label={placeholder}
          pageNumberScale={pageNumberScale}
          collaboratorColor={collaboratorColor}
        />
      )}
    </>
  );
};

export default DateRangeElement;
